import React, { useState, useRef } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import { LogOut, Camera } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import { resizeImageToBase64 } from '../utils/imageUtils';
import { geocodeCity } from '../utils/geo';
import Button from './ui/Button';
import StarRating from './ui/StarRating';
import CityAutocomplete from './ui/CityAutocomplete';
import { EMOJI_OPTIONS } from '../constants/data';
import TeamPicker from './ui/TeamPicker';

const AvatarPickerGrid = ({ value, onChange }) => (
  <div className="grid grid-cols-8 gap-1.5 bg-slate-50 p-3 rounded-xl border border-slate-200">
    {EMOJI_OPTIONS.map(emoji => (
      <button
        key={emoji}
        type="button"
        onClick={() => onChange(emoji)}
        className={`h-9 w-full flex items-center justify-center text-lg rounded-lg transition-colors ${value === emoji ? 'bg-blue-500' : 'hover:bg-slate-200'}`}
      >
        {emoji}
      </button>
    ))}
  </div>
);

const maskPhone = (v) => v.replace(/\D/g, '').replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2').slice(0, 15);

const PhoneInput = ({ value, onChange }) => (
  <div className="flex">
    <span className="flex items-center gap-1 px-3 bg-slate-100 border border-r-0 border-slate-200 rounded-l-xl text-sm font-medium text-slate-600 whitespace-nowrap">
      🇧🇷 +55
    </span>
    <input
      type="tel"
      className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-r-xl outline-none"
      placeholder="(xx) 9xxxx-xxxx"
      value={value}
      maxLength={15}
      onChange={e => onChange(maskPhone(e.target.value))}
    />
  </div>
);

/**
 * Shown when a user is authenticated but has no Firestore profile yet.
 * This can happen if the Firestore write failed during registration.
 */
const ProfileSetupOverlay = () => {
  const { createProfile } = useAppContext();
  const { showError } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: '',
    position: 'Meia',
    manualRating: 5,
    heartTeam: 'Sem Time',
    dominantFoot: 'Destro',
    photo: '👤',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    setSubmitting(true);
    try {
      let photoURL = null;
      if (photoFile) photoURL = await resizeImageToBase64(photoFile);
      const coords = formData.city ? await geocodeCity(formData.city).catch(() => null) : null;
      await createProfile({
        name: formData.name.trim(),
        phone: formData.phone,
        email: auth.currentUser?.email || '',
        photo: formData.photo,
        photoURL,
        position: formData.position,
        manualRating: parseFloat(formData.manualRating),
        communityRating: null,
        heartTeam: formData.heartTeam,
        dominantFoot: formData.dominantFoot,
        city: formData.city || '',
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
      });
    } catch {
      showError('Erro ao salvar perfil. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900 z-50 overflow-y-auto"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex flex-col items-center justify-center min-h-full p-6">
        <img src="/icone.png" alt="Pelada Match" className="w-16 h-16 mb-3 drop-shadow-lg" />
        <h1 className="text-xl font-bold text-white mb-1">pelada<span className="text-blue-400">match</span></h1>
        <p className="text-slate-400 text-xs mb-6">Complete seu cadastro para começar</p>

        <form onSubmit={handleSubmit} className="w-full max-w-sm bg-white rounded-xl p-6 shadow-xl space-y-4">
          <h2 className="text-lg font-bold text-slate-800 text-center">Complete seu cadastro</h2>

          {/* Foto de perfil */}
          <div className="flex justify-center">
            <div className="relative cursor-pointer" onClick={() => fileInputRef.current?.click()}>
              {photoPreview ? (
                <img src={photoPreview} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-slate-200" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center text-3xl border-2 border-slate-200">{formData.photo}</div>
              )}
              <div className="absolute bottom-0 right-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center shadow-sm">
                <Camera className="w-3 h-3 text-white" />
              </div>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={e => {
              const file = e.target.files?.[0];
              if (!file) return;
              setPhotoFile(file);
              const reader = new FileReader();
              reader.onload = ev => setPhotoPreview(ev.target.result);
              reader.readAsDataURL(file);
            }} className="hidden" />
          </div>

          <AvatarPickerGrid value={formData.photo} onChange={v => setFormData({ ...formData, photo: v })} />

          <input
            className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none"
            placeholder="Nome *"
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            required
          />

          <PhoneInput
            value={formData.phone}
            onChange={v => setFormData({ ...formData, phone: v })}
          />

          <CityAutocomplete
            value={formData.city}
            onChange={v => setFormData({ ...formData, city: v })}
          />

          <div className="grid grid-cols-2 gap-3">
            <select className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={formData.position} onChange={e => setFormData({ ...formData, position: e.target.value })}>
              <option value="Goleiro">Goleiro</option>
              <option value="Defensor">Defensor</option>
              <option value="Meia">Meia</option>
              <option value="Atacante">Atacante</option>
            </select>
            <TeamPicker value={formData.heartTeam} onChange={team => setFormData({ ...formData, heartTeam: team })} />
          </div>

          <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 flex flex-col items-center">
            <label className="text-xs font-bold text-slate-500 mb-2">Seu Nível (Auto-avaliação)</label>
            <StarRating rating={formData.manualRating} setRating={v => setFormData({ ...formData, manualRating: v })} />
          </div>

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? 'Salvando...' : 'Entrar na Pelada ⚽'}
          </Button>
        </form>

        <button
          type="button"
          onClick={() => signOut(auth)}
          className="mt-4 flex items-center gap-2 text-slate-500 text-sm hover:text-slate-300 transition-colors"
        >
          <LogOut className="w-4 h-4" /> Sair da conta
        </button>
      </div>
    </div>
  );
};

export default ProfileSetupOverlay;
